// Cartão de contato das Mensagens (0.38): cada pessoa põe a própria foto e, se quiser, telefone, e-mail e uma frase.
// Só veem: quem conversa com a pessoa, os amigos e quem divide um grupo do app (o servidor confere).
import { useEffect, useSyncExternalStore } from 'react';
import { Platform } from 'react-native';
import { supabase } from './supabase';
import { DEMO } from './env';
import { uid } from './date';
import { t } from './i18n';
import { lerBase64 } from './anexos';
import type { Anexo } from '../store/types';

export type Contato = { user_id: string; foto: string | null; telefone: string | null; email: string | null; frase: string | null };
export const FRASE_MAX = 90;

let cache: Record<string, Contato | null> = {};   // null = a pessoa não preencheu (ou não pode ser vista)
const ouv = new Set<() => void>();
const avisar = () => { cache = { ...cache }; ouv.forEach(f => f()); };
const assinar = (f: () => void) => { ouv.add(f); return () => { ouv.delete(f); }; };

const demo: Record<string, Contato> = {
  'u-ana': { user_id: 'u-ana', foto: null, telefone: '+55 41 99999-0000', email: 'ana@exemplo.com', frase: 'Viajando por Lisboa ✈️' },
};

// pedidos juntos: várias telas pedem contatos ao mesmo tempo, vai uma consulta só
let fila = new Set<string>();
let timer: ReturnType<typeof setTimeout> | null = null;
const pedindo = new Set<string>();
function pedir(id: string) {
  if (!id || id in cache || pedindo.has(id)) return;
  fila.add(id); pedindo.add(id);
  if (!timer) timer = setTimeout(buscar, 60);
}
async function buscar() {
  timer = null;
  const ids = [...fila]; fila = new Set();
  if (!ids.length) return;
  if (DEMO) { ids.forEach(i => { cache[i] = demo[i] ?? null; pedindo.delete(i); }); avisar(); return; }
  if (!supabase) return;
  const { data, error } = await supabase.from('contato_perfil').select('user_id, foto, telefone, email, frase').in('user_id', ids);
  ids.forEach(i => pedindo.delete(i));
  if (error) return;   // servidor ainda sem a tabela: tenta de novo na próxima tela
  ids.forEach(i => { cache[i] = null; });
  for (const c of (data ?? []) as Contato[]) cache[c.user_id] = c;
  avisar();
}

/** Contato de uma pessoa (undefined enquanto carrega, null se ela não preencheu). */
export function useContato(id: string | null | undefined): Contato | null | undefined {
  const c = useSyncExternalStore(assinar, () => cache, () => cache);
  useEffect(() => { if (id) pedir(id); }, [id]);
  return id ? c[id] : null;
}
/** Esquece o que está guardado (ao abrir a lista de novo, busca as fotos e frases novas). */
export function recarregarContatos(ids?: string[]) {
  if (ids) ids.forEach(i => { delete cache[i]; }); else cache = {};
  avisar();
}

const urls = new Map<string, { url: string; ate: number }>();
const esperando = new Map<string, Promise<string | null>>();
/** Endereço temporário da foto (1 hora). */
export function urlFoto(path: string): Promise<string | null> {
  const c = urls.get(path);
  if (c && c.ate > Date.now()) return Promise.resolve(c.url);
  if (DEMO || !supabase) return Promise.resolve(null);
  if (esperando.has(path)) return esperando.get(path)!;
  const p = supabase.storage.from('perfil').createSignedUrl(path, 3600).then(({ data }) => {
    esperando.delete(path);
    if (data?.signedUrl) urls.set(path, { url: data.signedUrl, ate: Date.now() + 50 * 60000 });
    return data?.signedUrl ?? null;
  }, () => { esperando.delete(path); return null; });
  esperando.set(path, p);
  return p;
}

const precisaSql = (e: any) => /relation|does not exist|schema cache|PGRST20|bucket/i.test(String(e?.message ?? e ?? ''));
const erroAmigavel = (e: any) => (precisaSql(e) ? t('O servidor ainda não está preparado para o cartão de contato. Rode o 19-LIGAR-CONTATO.bat.') : String(e?.message ?? e));

async function eu(): Promise<string> {
  if (DEMO) return 'eu';
  const { data } = await supabase!.auth.getUser();
  if (!data.user) throw new Error(t('Entre na sua conta de novo.'));
  return data.user.id;
}

export async function meuContato(): Promise<Contato> {
  const id = await eu();
  const vazio: Contato = { user_id: id, foto: null, telefone: null, email: null, frase: null };
  if (DEMO) return (cache[id] as Contato) ?? vazio;
  const { data, error } = await supabase!.from('contato_perfil').select('user_id, foto, telefone, email, frase').eq('user_id', id).maybeSingle();
  if (error) throw new Error(erroAmigavel(error));
  return (data as Contato) ?? vazio;
}

const limpo = (s: string | null | undefined, max: number) => { const x = (s ?? '').trim().slice(0, max); return x || null; };

export async function salvarMeuContato(p: { telefone: string; email: string; frase: string; foto?: string | null }) {
  const id = await eu();
  const linha: Contato = { user_id: id, telefone: limpo(p.telefone, 30), email: limpo(p.email, 120), frase: limpo(p.frase, FRASE_MAX),
    foto: p.foto === undefined ? (cache[id]?.foto ?? null) : p.foto };
  if (linha.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(linha.email)) throw new Error(t('Confira o e-mail.'));
  if (!DEMO) {
    const { error } = await supabase!.from('contato_perfil').upsert({ ...linha, atualizado_em: new Date().toISOString() });
    if (error) throw new Error(erroAmigavel(error));
  }
  cache[id] = linha; avisar();
  return linha;
}

const bytesDe = (b64: string) => { const bin = atob(b64); const u = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i); return u; };

/** Diminui a foto para 512 px (quando o app tem o módulo de imagem); senão envia como está (até 8 MB). */
async function reduzir(a: Anexo): Promise<{ b64: string; mime: string }> {
  const temModulo = Platform.OS === 'web' || !!(globalThis as any).expo?.modules?.ExpoImageManipulator;
  const origem = a.uri ?? a.dado;
  if (temModulo && origem) {
    try {
      const IM = require('expo-image-manipulator');
      const r = await IM.manipulateAsync(origem, [{ resize: { width: 512 } }], { compress: 0.8, format: IM.SaveFormat.JPEG, base64: true });
      if (r?.base64) return { b64: r.base64, mime: 'image/jpeg' };
    } catch { /* segue com a original */ }
  }
  const b64 = await lerBase64(a);
  if (!b64) throw new Error(t('Não foi possível ler o arquivo.'));
  return { b64, mime: a.mime || 'image/jpeg' };
}

/** Troca a minha foto (galeria ou câmera). Apaga a anterior. */
export async function trocarMinhaFoto(a: Anexo, atual: Contato) {
  const id = await eu();
  if (DEMO) return salvarMeuContato({ telefone: atual.telefone ?? '', email: atual.email ?? '', frase: atual.frase ?? '', foto: null });
  const { b64, mime } = await reduzir(a);
  const bytes = bytesDe(b64);
  if (bytes.length > 8 * 1024 * 1024) throw new Error(t('A foto passa de 8 MB.'));
  const path = `${id}/${uid()}.${mime === 'image/png' ? 'png' : 'jpg'}`;
  const up = await supabase!.storage.from('perfil').upload(path, bytes, { contentType: mime, upsert: false });
  if (up.error) throw new Error(erroAmigavel(up.error));
  const r = await salvarMeuContato({ telefone: atual.telefone ?? '', email: atual.email ?? '', frase: atual.frase ?? '', foto: path });
  if (atual.foto) supabase!.storage.from('perfil').remove([atual.foto]).catch(() => {});
  return r;
}
export async function tirarMinhaFoto(atual: Contato) {
  const r = await salvarMeuContato({ telefone: atual.telefone ?? '', email: atual.email ?? '', frase: atual.frase ?? '', foto: null });
  if (atual.foto && !DEMO) supabase!.storage.from('perfil').remove([atual.foto]).catch(() => {});
  return r;
}

/** Número só com dígitos (e o + do país) para ligar ou abrir o WhatsApp. */
export const soDigitos = (tel: string) => tel.replace(/(?!^\+)[^\d]/g, '');
